// General-purpose hook for calculating optimal grid layout

import { useState, useEffect, useCallback, RefObject } from 'react';

/**
 * Configuration for grid layout calculation
 */
interface GridLayoutConfig {
  minSize?: number; // Minimum item size in pixels
  maxSize?: number; // Maximum item size in pixels
  rowGap?: number; // Gap between rows in pixels
  columnGap?: number; // Gap between columns in pixels
  layoutType?: 'auto' | 'square' | 'row' | 'column' | 'grid'; // Layout strategy
  maxColumns?: number; // Maximum number of columns (0 = unlimited)
  maxRows?: number; // Maximum number of rows (0 = unlimited)
}

/**
 * Return type for grid layout calculation
 */
interface GridLayoutResult {
  itemSize: number; // Calculated optimal item size
  gridColumns: number; // Number of grid columns
  gridRows: number; // Number of grid rows
  rowGap: number; // Gap between rows
  columnGap: number; // Gap between columns
  containerWidth: number; // Container width
  containerHeight: number; // Container height
}

/**
 * Calculate optimal grid configuration for given item count and container size
 */
function calculateOptimalLayout(
  itemCount: number,
  containerWidth: number,
  containerHeight: number,
  config: Required<GridLayoutConfig>
): Omit<GridLayoutResult, 'containerWidth' | 'containerHeight'> {
  if (itemCount === 0) {
    return {
      itemSize: config.minSize,
      gridColumns: 1,
      gridRows: 1,
      rowGap: config.rowGap,
      columnGap: config.columnGap,
    };
  }

  const {
    minSize,
    maxSize,
    rowGap,
    columnGap,
    layoutType,
    maxColumns,
    maxRows,
  } = config;

  // For single row layout
  if (layoutType === 'row') {
    const availableWidth = containerWidth - (itemCount - 1) * columnGap;
    const calculatedSize = Math.floor(availableWidth / itemCount);
    const itemSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

    return {
      itemSize,
      gridColumns: itemCount,
      gridRows: 1,
      rowGap,
      columnGap,
    };
  }

  // For single column layout
  if (layoutType === 'column') {
    const availableHeight = containerHeight - (itemCount - 1) * rowGap;
    const calculatedSize = Math.floor(availableHeight / itemCount);
    const itemSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

    return {
      itemSize,
      gridColumns: 1,
      gridRows: itemCount,
      rowGap,
      columnGap,
    };
  }

  // For square layout, force square grid
  if (layoutType === 'square') {
    const gridSize = Math.ceil(Math.sqrt(itemCount));
    const effectiveColumns = Math.min(gridSize, maxColumns || gridSize);
    const effectiveRows = Math.min(gridSize, maxRows || gridSize);

    const availableWidth = containerWidth - (effectiveColumns - 1) * columnGap;
    const availableHeight = containerHeight - (effectiveRows - 1) * rowGap;

    const maxSizeByWidth = Math.floor(availableWidth / effectiveColumns);
    const maxSizeByHeight = Math.floor(availableHeight / effectiveRows);
    const calculatedSize = Math.min(maxSizeByWidth, maxSizeByHeight);
    const itemSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

    return {
      itemSize,
      gridColumns: effectiveColumns,
      gridRows: effectiveRows,
      rowGap,
      columnGap,
    };
  }

  // Auto layout (including center) - find optimal configuration
  let bestLayout = {
    itemSize: minSize,
    gridColumns: 1,
    gridRows: itemCount,
    rowGap,
    columnGap,
    score: 0, // Higher is better
  };

  // Try different grid configurations
  const maxCols = maxColumns || Math.ceil(Math.sqrt(itemCount * 2));
  const maxRowsLimit = maxRows || Math.ceil(itemCount / 1);

  for (let cols = 1; cols <= Math.min(maxCols, itemCount); cols++) {
    const rows = Math.ceil(itemCount / cols);
    if (maxRowsLimit > 0 && rows > maxRowsLimit) continue;

    const availableWidth = containerWidth - (cols - 1) * columnGap;
    const availableHeight = containerHeight - (rows - 1) * rowGap;

    const maxSizeByWidth = Math.floor(availableWidth / cols);
    const maxSizeByHeight = Math.floor(availableHeight / rows);
    const calculatedSize = Math.min(maxSizeByWidth, maxSizeByHeight);
    const itemSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

    // Score this configuration (prefer larger items and good space utilization)
    const sizeScore = itemSize / maxSize; // 0-1, higher is better
    const utilizationScore = itemCount / (cols * rows); // 0-1, higher is better

    // For grid layout, prefer more balanced grids (closer to square)
    const balanceScore =
      layoutType === 'grid'
        ? 1 - Math.abs(cols - rows) / Math.max(cols, rows) // 0-1, higher is more balanced
        : 0.5; // Neutral for auto layout

    const score = sizeScore * 0.5 + utilizationScore * 0.3 + balanceScore * 0.2;

    if (score > bestLayout.score) {
      bestLayout = {
        itemSize,
        gridColumns: cols,
        gridRows: rows,
        rowGap,
        columnGap,
        score,
      };
    }
  }

  return {
    itemSize: bestLayout.itemSize,
    gridColumns: bestLayout.gridColumns,
    gridRows: bestLayout.gridRows,
    rowGap: bestLayout.rowGap,
    columnGap: bestLayout.columnGap,
  };
}

/**
 * Hook for calculating optimal grid layout based on container size and item count
 *
 * @param itemCount - Number of items to display
 * @param containerRef - Reference to the container element
 * @param config - Layout configuration options
 * @returns Grid layout properties
 */
export function useGridLayout(
  itemCount: number,
  containerRef: RefObject<HTMLElement | null>,
  config: GridLayoutConfig = {}
): GridLayoutResult {
  const {
    minSize = 120,
    maxSize = 240,
    rowGap = 16,
    columnGap = 16,
    layoutType = 'auto',
    maxColumns = 0,
    maxRows = 0,
  } = config;

  const [layout, setLayout] = useState<GridLayoutResult>(() => ({
    itemSize: Math.min(maxSize, Math.max(minSize, 160)),
    gridColumns: Math.ceil(Math.sqrt(itemCount || 1)),
    gridRows: Math.ceil(
      (itemCount || 1) / Math.ceil(Math.sqrt(itemCount || 1))
    ),
    rowGap,
    columnGap,
    containerWidth: 800,
    containerHeight: 600,
  }));

  const calculateLayout = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const containerWidth = rect.width || 800;
    const containerHeight = rect.height || 600;

    const optimalLayout = calculateOptimalLayout(
      itemCount,
      containerWidth,
      containerHeight,
      { minSize, maxSize, rowGap, columnGap, layoutType, maxColumns, maxRows }
    );

    setLayout({
      ...optimalLayout,
      containerWidth,
      containerHeight,
    });
  }, [
    itemCount,
    minSize,
    maxSize,
    rowGap,
    columnGap,
    layoutType,
    maxColumns,
    maxRows,
    containerRef,
  ]);

  // Calculate layout on mount and when dependencies change
  useEffect(() => {
    calculateLayout();
  }, [calculateLayout]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => calculateLayout();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateLayout]);

  // Recalculate when container becomes available
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calculateLayout();
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [calculateLayout, containerRef]);

  return layout;
}
